import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useAuth } from "../../AuthContext";
import { envEndPoints } from "../../config";
const EditClient = ({ clientId, onClose, onSaveSuccess }) => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientName, setClientName] = useState("");
  const [description, setDescription] = useState("");
  const [industries, setIndustries] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [keywordTypes, setKeywordTypes] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState({});
  const [xHandles, setXHandles] = useState([]);
  const [facebookHandles, setFacebookHandles] = useState([]);
  const [instagramHandles, setInstagramHandles] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const authHeaders = { headers: getAuthHeader() };
        const responses = await Promise.all([
          axios.get(`${envEndPoints().baseUrl}/industries/`),
          axios.get(`${envEndPoints().baseUrl}/keywords/`),
          axios.get(`${envEndPoints().baseUrl}/keyword-types/`),
          axios.get(`${envEndPoints().baseUrl}/users/`),
          axios.get(`${envEndPoints().baseUrl}/clients/?client_id=${clientId}`),
          axios.get(`${envEndPoints().baseUrl}/client-owners/?client_id=${clientId}`),
          axios.get(`${envEndPoints().baseUrl}/client-keywords/?client_id=${clientId}`),
          axios.get(`${envEndPoints().baseUrl}/client-industries/?client_id=${clientId}`),
          axios.get(`${envEndPoints().baseUrl}/client-xhandles/?client_id=${clientId}`, authHeaders),
          axios.get(`${envEndPoints().baseUrl}/client-facebook-handles/?client_id=${clientId}`, authHeaders),
          axios.get(`${envEndPoints().baseUrl}/client-instagram-handles/?client_id=${clientId}`, authHeaders),
        ]);

        const [
          industriesRes,
          keywordsRes,
          keywordTypesRes,
          usersRes,
          clientRes,
          clientOwnersRes,
          clientKeywordsRes,
          clientIndustriesRes,
          xHandlesRes,
          facebookHandlesRes,
          instagramHandlesRes,
        ] = responses;

        setIndustries(industriesRes.data.industries || []);
        setKeywords(keywordsRes.data.keywords || []);
        setKeywordTypes(keywordTypesRes.data || []);

        const usersWithFullName = usersRes.data.users.map((user) => ({
          ...user,
          fullName:
            `${user.firstname || ""} ${user.surname || ""}`.trim() || "Unknown",
        }));

        setUsers(usersWithFullName);

        if (clientRes.data.length > 0) {
          const clientData = clientRes.data[0];
          setClientName(clientData.name || "");
          setDescription(clientData.description || "");
        } else {
          setError("No client data found");
        }

        // Load handles from dedicated endpoints
        setXHandles(xHandlesRes.data[0]?.handles?.map(h => h.handle) || []);
        setFacebookHandles(facebookHandlesRes.data[0]?.handles?.map(h => h.handle) || []);
        setInstagramHandles(instagramHandlesRes.data[0]?.handles?.map(h => h.handle) || []);

        const clientOwnerIds = clientOwnersRes.data.map((owner) => owner.id);
        const clientUsers = usersWithFullName.filter((user) =>
          clientOwnerIds.includes(user.id)
        );
        setSelectedUsers(clientUsers);

        const clientIndustries =
          clientIndustriesRes.data[0]?.industries.map((industryName) =>
            industriesRes.data.industries.find(
              (industry) => industry.name === industryName
            )
          ) || [];
        setSelectedIndustries(clientIndustries);

        const clientKeywordsData = clientKeywordsRes.data[0]?.keywords || [];
        setSelectedKeywords(
          keywordTypesRes.data.reduce((acc, type) => {
            const keywordsByType = clientKeywordsData
              .filter((kw) => kw.type === type.name)
              .map((kw) => ({
                name: kw.name,
                id:
                  keywordsRes.data.keywords.find((k) => k.name === kw.name)
                    ?.id || null,
              }));
            return { ...acc, [type.id]: keywordsByType };
          }, {})
        );
      } catch (error) {
        setError("Failed to fetch data: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [clientId, getAuthHeader]);

  const checkAndAddKeyword = async (keywordName) => {
    try {
      // Check if keyword exists
      const existingKeyword = keywords.find(
        (keyword) => keyword.name === keywordName
      );
      if (existingKeyword) {
        return existingKeyword.id;
      }

      // Add new keyword
      const response = await axios.post(`${envEndPoints().baseUrl}/keywords`, {
        name: keywordName,
      }, { headers: getAuthHeader() });
      const newKeyword = response.data;
      setKeywords((prevKeywords) => [
        ...prevKeywords,
        { name: newKeyword.name, id: newKeyword.id },
      ]);
      return newKeyword.id;
    } catch (error) {
      setError("Failed to add keyword: " + error.message);
      throw error;
    }
  };

  const handleKeywordChange = (typeId) => async (event, newValue) => {
    const updatedKeywords = await Promise.all(
      newValue.map(async (keyword) => {
        const keywordName =
          typeof keyword === "string" ? keyword : keyword.name;
        const keywordId = await checkAndAddKeyword(keywordName);
        return {
          id: keywordId,
          name: keywordName,
          typeId: typeId,
        };
      })
    );

    setSelectedKeywords((prev) => ({
      ...prev,
      [typeId]: updatedKeywords,
    }));
  };

  const generateSearchQueries = async (clientId) => {
    try {
      const response = await axios.post(
        `${envEndPoints().baseUrl}/gen-search-queries/${clientId}`,
        {},
        { headers: getAuthHeader() }
      );
      console.log("Search queries generated:", response.data);
      return response.data; // Return the data to be used or stored by the caller if needed
    } catch (error) {
      console.error("Error generating search queries:", error);
      if (error.response) {
        // Log detailed response if available
        console.log("Response status:", error.response.status);
        console.log("Response headers:", error.response.headers);
        console.log("Response data:", error.response.data);
      }
      throw error; // Re-throw the error if you want to handle it further up the call stack, for example in handleSubmit
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const ownerIds = selectedUsers.map((user) => user.id);
      const industryIds = selectedIndustries.map((industry) => industry.id);

      const authHeaders = { headers: getAuthHeader() };

      // Update client details
      await axios.put(`${envEndPoints().baseUrl}/clients/${clientId}`, {
        name: clientName,
        description: description,
        ownerIds: ownerIds,
        industryIds: industryIds,
      }, authHeaders);

      // Prepare keywords data
      const keywordsData = Object.entries(selectedKeywords).flatMap(
        ([typeId, keywords]) =>
          keywords.map((keyword) => ({
            keywordId: keyword.id,
            keywordTypeId: parseInt(typeId),
          }))
      );

      // Update client keywords
      await axios.put(`${envEndPoints().baseUrl}/client-keywords/`, {
        clientId: parseInt(clientId),
        keywords: keywordsData,
      }, authHeaders);

      // Update X (Twitter) handles
      await axios.put(`${envEndPoints().baseUrl}/client-xhandles/`, {
        clientId: parseInt(clientId),
        handles: xHandles.map((h) => String(h).trim().replace(/^@/, "")).filter(Boolean),
      }, authHeaders);

      // Update Facebook handles
      await axios.put(`${envEndPoints().baseUrl}/client-facebook-handles/`, {
        clientId: parseInt(clientId),
        handles: facebookHandles.map((h) => String(h).trim().replace(/^@/, "")).filter(Boolean),
      }, authHeaders);

      // Update Instagram handles
      await axios.put(`${envEndPoints().baseUrl}/client-instagram-handles/`, {
        clientId: parseInt(clientId),
        handles: instagramHandles.map((h) => String(h).trim().replace(/^@/, "")).filter(Boolean),
      }, authHeaders);

      // Generate search queries (non-blocking: don't fail the whole save if this errors)
      // Previously -> const searchQueries = await generateSearchQueries(clientId); 
      // Was causing error in try catch block. Need to review!
      try {
        const searchQueries = await generateSearchQueries(clientId); 
      } catch (searchError) {
        console.warn("Search queries could not be regenerated:", searchError.message);
      }

      setSuccessMessage("Client updated successfully!");
      if (onSaveSuccess) await onSaveSuccess();
      onClose(); // Close the form/modal
    } catch (error) {
      setError(
        "Failed to update client: " +
          (error.response ? error.response.data.error : error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <CircularProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Client Name"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            variant="outlined"
            error={!!error}
            helperText={error}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            variant="outlined"
            error={!!error}
            helperText={error}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={users}
            getOptionLabel={(option) => option.fullName || "Unknown"}
            value={selectedUsers}
            onChange={(event, newValue) => setSelectedUsers(newValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Client Owners" variant="outlined" />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={industries}
            getOptionLabel={(option) => option.name || ""}
            value={selectedIndustries}
            onChange={(event, newValue) => setSelectedIndustries(newValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Industries" variant="outlined" />
            )}
          />
        </Grid>
        {keywordTypes.map((type) => (
          <Grid item xs={12} key={type.id}>
            <Autocomplete
              multiple
              freeSolo
              options={keywords.map((keyword) => keyword.name)}
              value={selectedKeywords[type.id].map((keyword) => keyword.name)}
              onChange={handleKeywordChange(type.id)}
              renderInput={(params) => (
                <TextField {...params} label={type.name} variant="outlined" />
              )}
            />
          </Grid>
        ))}
        {/* Where X Handles input box is rendered */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={xHandles}
            onChange={(event, newValue) => setXHandles(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="X Handles (Previously Twitter)"
                variant="outlined"
              />
            )}
          />
        </Grid>
        {/* Where Facebook input box is rendered */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={facebookHandles}
            onChange={(event, newValue) => setFacebookHandles(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Facebook Handles"
                variant="outlined"
              />
            )}
          />
        </Grid>
        {/* Where Instagram input box is rendered */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={instagramHandles}
            onChange={(event, newValue) => setInstagramHandles(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Instagram Handles"
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default EditClient;
